<template>
  <div class="contest-apply" v-if="loaded">
    <a-card :bordered="false" class="page-top" :class="{ 'page-top-banner': showCustomHeroBanner }">
      <img
        v-if="showCustomHeroBanner"
        class="hero-banner-image"
        :src="heroBannerSrc"
        alt="报名顶部主图"
      />
      <template v-else>
        <div class="hero-title">{{ contestTitle }}</div>
        <div class="hero-meta" v-if="activityMeta">
          <span>报名截止：{{ formatDate(activityMeta.endTime) }}</span>
          <span v-if="editMode" class="meta-tag">编辑已报名作品</span>
        </div>
      </template>
    </a-card>

    <a-card v-if="!activityMeta" :bordered="false" class="state-card">
      <a-alert type="warning" show-icon message="当前赛事不存在或已下线，请返回后重试。" />
    </a-card>

    <a-card v-else-if="submitSuccess" :bordered="false" class="result-card">
      <div class="result-title">{{ editMode ? "作品修改成功" : "作品报名成功" }}</div>
      <div class="result-desc">可前往我的-参赛作品中修改作品信息</div>
      <div class="result-actions">
        <a-button type="primary" @click="goToUserWorks">前往参赛作品</a-button>
        <a-button @click="goBackToContest">返回赛事详情</a-button>
      </div>
    </a-card>

    <a-card v-else :bordered="false" class="form-card">
      <div class="step-bar">
        <div
          v-for="(item, index) in stepTitles"
          :key="item"
          class="step-item"
          :class="{ active: currentStep === index + 1 }"
        >
          <span class="step-index">{{ index + 1 }}</span>
          <span>{{ item }}</span>
        </div>
      </div>

      <template v-if="currentStep === 1">
        <section class="form-section">
          <div class="section-title">选择参赛板块</div>
          <div class="option-row">
            <div
              v-for="item in plateOptions"
              :key="item.id"
              class="option-pill"
              :class="{ selected: Number(form.plateId) === Number(item.id) }"
              @click="form.plateId = Number(item.id)"
            >
              {{ item.name }}
            </div>
          </div>
        </section>

        <section v-if="isGraduationContest" class="form-section">
          <div class="section-title">选择参赛门类</div>
          <div class="option-row graduation-category-row">
            <div
              v-for="item in categoryOptions"
              :key="item.id"
              class="option-pill"
              :class="{ selected: Number(form.categoryId) === Number(item.id) }"
              @click="form.categoryId = Number(item.id)"
            >
              {{ item.name }}
            </div>
          </div>
        </section>

      </template>

      <template v-else-if="currentStep === 2">
        <section v-if="!isGraduationContest" class="form-section">
          <div class="section-title">选择参赛者身份</div>
          <div class="identity-card-row">
            <div
              v-for="item in participantIdentityOptions"
              :key="item.value"
              class="identity-card"
              :class="{ selected: form.participantProfile.identity === item.value }"
              @click="selectParticipantIdentity(item.value)"
            >
              <div class="identity-card-title">{{ item.label }}</div>
              <div class="identity-card-desc">{{ item.description }}</div>
            </div>
          </div>
        </section>

        <section v-if="isStudentParticipant" class="form-section">
          <div class="section-title">填写院校与指导老师</div>
          <div class="grid two-col">
            <div class="field">
              <label>学校/院校</label>
              <a-select
                v-model="form.schoolId"
                show-search
                option-filter-prop="children"
                :filter-option="filterSchoolOption"
                placeholder="请选择学校/院校"
              >
                <a-select-option
                  v-for="item in schoolSelectOptions"
                  :key="item.id"
                  :value="item.id"
                >
                  {{ item.title }}
                </a-select-option>
              </a-select>
            </div>
            <div v-if="form.schoolId === otherSchoolOptionValue" class="field">
              <label>其他院校</label>
              <a-input v-model.trim="form.participantProfile.schoolNameCustom" placeholder="请输入学校/院校名称" />
            </div>
            <div class="field wide">
              <label>学历层次</label>
              <a-radio-group v-model="form.participantProfile.educationLevel">
                <a-radio v-for="item in educationLevelOptions" :key="item" :value="item">{{ item }}</a-radio>
              </a-radio-group>
            </div>
            <div class="field wide">
              <label>指导老师</label>
              <div class="mentor-tip">最多可填写 3 位指导老师</div>
              <div class="grid three-col">
                <a-input v-model.trim="form.mentors[0]" placeholder="选填，请输入指导老师1姓名" />
                <a-input v-model.trim="form.mentors[1]" placeholder="选填，请输入指导老师2姓名" />
                <a-input v-model.trim="form.mentors[2]" placeholder="选填，请输入指导老师3姓名" />
              </div>
            </div>
          </div>
        </section>

        <section v-else class="form-section">
          <div class="section-title">填写从业信息</div>
          <div class="grid two-col">
            <div class="field">
              <label>当前单位/公司</label>
              <a-input v-model.trim="form.participantProfile.companyName" placeholder="选填，请输入当前单位或公司名称" />
            </div>
            <div class="field">
              <label>从业方向/领域</label>
              <a-select v-model="form.participantProfile.industryField" placeholder="请选择从业方向/领域">
                <a-select-option
                  v-for="item in industryFieldOptions"
                  :key="item"
                  :value="item === '其他' ? otherOptionValue : item"
                >
                  {{ item }}
                </a-select-option>
              </a-select>
            </div>
            <div v-if="form.participantProfile.industryField === otherOptionValue" class="field wide">
              <label>其他从业方向/领域</label>
              <a-input v-model.trim="form.participantProfile.industryFieldCustom" placeholder="请注明从业方向或领域" />
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="section-title">参赛形式</div>
          <a-radio-group v-model="form.creatorType">
            <a-radio value="personal">个人参赛</a-radio>
            <a-radio value="team">团队参赛</a-radio>
          </a-radio-group>
        </section>

        <section class="form-section">
          <div class="section-title with-tip">
            <span>{{ form.creatorType === "team" ? "团队联系人" : "个人信息" }}</span>
            <span v-if="form.creatorType === 'team'" class="section-tip inline-tip">组委会会优先与团队负责人联络</span>
          </div>
          <div class="grid two-col">
            <div class="field">
              <label>作者姓名</label>
              <a-input v-model.trim="form.author.name" placeholder="请输入作者姓名" />
            </div>
            <div class="field">
              <label>国家-地区</label>
              <a-select
                v-model="form.author.countryRegion"
                show-search
                option-filter-prop="children"
                placeholder="请选择国家-地区"
              >
                <a-select-option
                  v-for="item in countryRegionSelectOptions"
                  :key="item.value"
                  :value="item.value"
                >
                  {{ item.label }}
                </a-select-option>
              </a-select>
            </div>
            <div v-if="form.author.countryRegion === otherCountryRegionValue" class="field">
              <label>其他国家-地区</label>
              <a-input v-model.trim="form.author.countryRegionCustom" placeholder="请输入国家或地区" />
            </div>
            <div class="field">
              <label>证件类型</label>
              <a-select v-model="form.author.certificateType" placeholder="请选择证件类型">
                <a-select-option v-for="item in certificateTypeOptions" :key="item" :value="item">
                  {{ item }}
                </a-select-option>
              </a-select>
            </div>
            <div class="field">
              <label>证件号</label>
              <a-input v-model.trim="form.author.idCard" placeholder="请输入证件号" />
            </div>
            <div class="field">
              <label>邮箱</label>
              <a-input v-model.trim="form.author.email" placeholder="请输入邮箱" />
            </div>
            <div class="field">
              <label>手机号</label>
              <a-input v-model.trim="form.author.phone" maxlength="11" placeholder="请输入手机号" />
            </div>
            <div class="field">
              <label>微信号</label>
              <a-input v-model.trim="form.author.wechat" placeholder="选填，请输入微信号" />
            </div>
            <div class="field wide">
              <div class="label-row">
                <label>作品寄回地址</label>
                <span class="section-tip inline-tip">作品寄回前，我们会与您最终确认。</span>
              </div>
              <a-input v-model.trim="form.author.address" placeholder="请输入作品寄回地址" />
            </div>
            <div class="field upload-field wide">
              <label>作者照片</label>
              <ImageUpload v-model="form.author.photo" :num="1" :width="120" :height="120" :max-size-mb="5" :activity-id="currentActivityId" />
            </div>
          </div>
        </section>

        <section v-if="form.creatorType === 'team'" class="form-section">
          <div class="section-title with-action">
            <div class="title-inline-group">
              <span>团队成员</span>
              <span class="section-tip inline-tip">根据组委会要求本次团队参赛队伍不得超过 4 人</span>
            </div>
            <a-button type="link" @click="addTeamMember">新增成员</a-button>
          </div>
          <div v-if="!form.teamMembers.length" class="section-tip">暂未添加团队成员。</div>
          <div v-for="(member, index) in form.teamMembers" :key="`member-${index}`" class="team-card">
            <div class="team-card-header">
              <span>团队成员 {{ index + 1 }}</span>
              <a-button type="link" @click="removeTeamMember(index)">删除</a-button>
            </div>
            <div class="grid two-col">
              <div class="field">
                <label>姓名</label>
                <a-input v-model.trim="member.name" placeholder="请输入姓名" />
              </div>
              <div class="field">
                <label>证件类型</label>
                <a-select v-model="member.certificateType" placeholder="请选择证件类型">
                  <a-select-option v-for="item in certificateTypeOptions" :key="item" :value="item">
                    {{ item }}
                  </a-select-option>
                </a-select>
              </div>
              <div class="field">
                <label>证件号</label>
                <a-input v-model.trim="member.idCard" placeholder="请输入证件号" />
              </div>
              <div class="field">
                <label>邮箱</label>
                <a-input v-model.trim="member.email" placeholder="请输入邮箱" />
              </div>
              <div class="field upload-field">
                <label>照片</label>
                <ImageUpload v-model="member.photo" :num="1" :width="120" :height="120" :max-size-mb="5" :activity-id="currentActivityId" />
              </div>
            </div>
          </div>
        </section>
      </template>

      <template v-else>
        <section class="form-section">
          <div class="section-title">作品基础信息</div>
          <div class="grid two-col">
            <div class="field wide">
              <label>作品名称</label>
              <a-input v-model.trim="form.work.name" placeholder="请输入作品名称" />
            </div>
          </div>

          <div class="inline-block">
            <label>用电</label>
            <div class="inline-controls">
              <a-radio-group v-model="form.work.needElectricity">
                <a-radio :value="0">否</a-radio>
                <a-radio :value="1">是</a-radio>
              </a-radio-group>
              <a-radio-group v-if="form.work.needElectricity === 1" v-model="form.work.powerSupplyType" class="power-supply-group">
                <a-radio v-for="item in powerSupplyOptions" :key="item" :value="item">{{ item }}</a-radio>
              </a-radio-group>
            </div>
          </div>

          <div class="inline-block">
            <label>展陈方式</label>
            <a-radio-group v-model="form.work.displayMode" class="display-mode-group">
              <div v-for="item in availableDisplayModeOptions" :key="item.value" class="display-mode-option">
                <a-radio :value="item.value">{{ item.label }}</a-radio>
                <a-input
                  v-if="item.value === otherDisplayModeValue && form.work.displayMode === otherDisplayModeValue"
                  v-model.trim="form.work.displayModeCustom"
                  class="display-mode-custom-input"
                  placeholder="请填写其他展示方式"
                />
              </div>
            </a-radio-group>
          </div>

          <div class="inline-block">
            <label>作品形式</label>
            <a-radio-group v-model="form.work.workType">
              <a-radio value="independent">单件作品</a-radio>
              <a-radio value="series">系列作品</a-radio>
            </a-radio-group>
          </div>

          <div class="grid two-col">
            <div class="field wide">
              <label>材质</label>
              <a-input v-model.trim="form.work.material" placeholder="请输入作品材质，中间使用“、”隔开" />
            </div>
          </div>
        </section>

        <section v-if="form.work.workType === 'independent'" class="form-section">
          <div class="section-title">单件作品信息</div>
          <div v-if="isClothingModelDisplay" class="grid two-col">
            <div class="field">
              <label>模特数量</label>
              <a-input-number v-model="form.work.modelCount" :min="1" :step="1" :precision="0" style="width: 100%" />
            </div>
          </div>
          <div v-else class="grid four-col">
            <div class="field">
              <label>长（cm）</label>
              <a-input-number v-model="form.work.length" :min="0" :step="0.1" :precision="2" style="width: 100%" />
            </div>
            <div class="field">
              <label>宽（cm）</label>
              <a-input-number v-model="form.work.width" :min="0" :step="0.1" :precision="2" style="width: 100%" />
            </div>
            <div class="field">
              <label>高（cm）</label>
              <a-input-number v-model="form.work.height" :min="0" :step="0.1" :precision="2" style="width: 100%" />
            </div>
            <div class="field">
              <label>重量（kg）</label>
              <a-input-number v-model="form.work.weight" :min="0" :step="0.1" :precision="2" style="width: 100%" />
            </div>
          </div>
          <div class="field upload-field wide">
            <label>作品图片（3-10 张，PNG/JPG，每张不超过 10MB）</label>
            <ImageUpload
              v-model="form.work.images"
              :num="10"
              :width="120"
              :height="120"
              :max-size-mb="10"
              :activity-id="currentActivityId"
            />
          </div>
        </section>

        <section v-else class="form-section">
          <div class="section-title">系列作品总览</div>
          <div class="grid two-col">
            <div class="field">
              <label>总长（cm）</label>
              <a-input-number v-model="form.work.overall.length" :min="0" :step="0.1" :precision="2" style="width: 100%" />
            </div>
            <div class="field">
              <label>总宽（cm）</label>
              <a-input-number v-model="form.work.overall.width" :min="0" :step="0.1" :precision="2" style="width: 100%" />
            </div>
            <div class="field">
              <label>总高（cm）</label>
              <a-input-number v-model="form.work.overall.height" :min="0" :step="0.1" :precision="2" style="width: 100%" />
            </div>
          </div>
          <div class="field upload-field wide">
            <label>总览图片（3-10 张，PNG/JPG，每张不超过 10MB）</label>
            <ImageUpload
              v-model="form.work.overall.images"
              :num="10"
              :width="120"
              :height="120"
              :max-size-mb="10"
              :activity-id="currentActivityId"
            />
          </div>

          <div class="section-title with-action series-title">
            <span>系列单件信息</span>
            <a-button type="link" @click="addSeriesItem">新增单件</a-button>
          </div>
          <div v-for="(item, index) in form.work.seriesItems" :key="`series-${index}`" class="team-card">
            <div class="team-card-header">
              <span>单件 {{ index + 1 }}</span>
              <a-button type="link" @click="removeSeriesItem(index)">删除</a-button>
            </div>
            <div class="grid four-col">
              <div class="field">
                <label>数量</label>
                <a-input-number v-model="item.count" :min="1" :step="1" :precision="0" style="width: 100%" />
              </div>
              <div class="field">
                <label>长（cm）</label>
                <a-input-number v-model="item.length" :min="0" :step="0.1" :precision="2" style="width: 100%" />
              </div>
              <div class="field">
                <label>宽（cm）</label>
                <a-input-number v-model="item.width" :min="0" :step="0.1" :precision="2" style="width: 100%" />
              </div>
              <div class="field">
                <label>高（cm）</label>
                <a-input-number v-model="item.height" :min="0" :step="0.1" :precision="2" style="width: 100%" />
              </div>
              <div class="field">
                <label>重量（kg）</label>
                <a-input-number v-model="item.weight" :min="0" :step="0.1" :precision="2" style="width: 100%" />
              </div>
            </div>
            <div class="field upload-field wide">
              <label>单件图片（3-10 张，PNG/JPG，每张不超过 10MB）</label>
              <ImageUpload
                v-model="item.images"
                :num="10"
                :width="120"
                :height="120"
                :max-size-mb="10"
                :activity-id="currentActivityId"
              />
            </div>
          </div>
        </section>

        <section class="form-section">
          <div class="section-title">补充资料</div>
          <div class="field upload-field wide">
            <div class="label-row">
              <label>原创承诺书（PDF/PNG/JPG）</label>
              <span class="upload-tip">请上传已签字版本</span>
              <a-button type="link" class="template-download-link" @click="downloadCommitmentTemplate">下载原创承诺书模板</a-button>
            </div>
            <WordUpload v-model="form.work.commitmentFile" />
          </div>
          <div class="field wide">
            <div class="label-row">
              <label>作品说明</label>
              <span class="section-tip inline-tip">作品说明请不超过100字，简要阐述创作理念与亮点。</span>
            </div>
            <div class="textarea-wrap">
              <a-textarea
                v-model.trim="form.work.description"
                :rows="6"
                placeholder="请填写作品创意、材质、工艺与展示说明"
              />
              <span class="textarea-counter">{{ descriptionLength }}/{{ maxWorkDescriptionLength }}</span>
            </div>
          </div>
        </section>
      </template>

      <div class="footer-actions">
        <a-button v-if="currentStep > 1" @click="prevStep">上一步</a-button>
        <a-button v-if="currentStep < 3" type="primary" @click="nextStep">下一步</a-button>
        <a-button v-else type="primary" :loading="submitting" @click="submit">{{ submitButtonText }}</a-button>
      </div>
    </a-card>
  </div>
</template>

<script>
import {
  activityDetail,
  getActivityCategoryList,
  getActivityPlateList,
  getActivitySchoolList,
  worksSignUp,
  worksSignUpEdit,
} from "@/api/Vote";
import { userEditVoteWorks } from "@/api/UserCenter";
import ImageUpload from "@/components/Upload/ImageUpload.vue";
import WordUpload from "@/components/Upload/WordUpload.vue";
import cashmereAssets from "@/constants/cashmereAssets";
import graduationAssets from "@/constants/graduationAssets";
import { resolveContestTimeline, resolveSignupBlockMessage } from "@/utils/contestTimeline";
import {
  DEFAULT_GRADUATION_ACTIVITY_ID,
  getContestKey,
  resolveContestActivityId,
} from "@/utils/contestActivity";
import { downloadFileByUrl, resolveCommitmentTemplateUrl } from "@/utils/fileDownload";
import { normalizeVoteOptionGroups } from "@/utils/voteMeta";

const STEP_TITLES = ["选择赛事信息", "填写参赛者信息", "填写作品信息"];
const DISPLAY_MODE_OPTIONS = [
  { label: "展台展示", value: "展台展示", panoramaType: 0 },
  { label: "悬吊悬挂", value: "悬吊悬挂", panoramaType: 2 },
  { label: "墙面展示", value: "墙面展示", panoramaType: 1 },
  { label: "模特展示", value: "模特展示", panoramaType: 0 },
  { label: "落地展示", value: "落地展示", panoramaType: 0 },
  { label: "其他", value: "其他", panoramaType: 0 },
];
const POWER_SUPPLY_OPTIONS = ["2孔", "3孔", "USB"];
const MAX_WORK_DESCRIPTION_LENGTH = 100;
const OTHER_OPTION_VALUE = "__other__";
const OTHER_SCHOOL_OPTION_VALUE = "__other_school__";

const WORK_DIMENSION_UNIT = "cm";
const OTHER_DISPLAY_MODE_VALUE = "其他";
const PARTICIPANT_IDENTITY_OPTIONS = [
  {
    value: "student",
    label: "学生",
    description: "填写院校、学历层次与指导老师信息",
  },
  {
    value: "professional",
    label: "非学生/在职人员",
    description: "填写单位信息与从业方向，适用于社会参赛者",
  },
];
const EDUCATION_LEVEL_OPTIONS = ["专科", "本科", "硕士研究生", "博士研究生"];
const INDUSTRY_FIELD_OPTIONS = [
  "工艺美术师/手工艺人",
  "设计师（产品/文创/首饰等）",
  "院校教师/研究人员",
  "企业/工作室管理人员",
  "艺术机构/画廊从业者",
  "自由职业者",
  "其他",
];
const COUNTRY_REGION_OPTIONS = [
  "中国大陆",
  "中国香港",
  "中国澳门",
  "中国台湾",
  "新加坡",
  "马来西亚",
  "日本",
  "韩国",
  "泰国",
  "越南",
  "美国",
  "加拿大",
  "英国",
  "法国",
  "德国",
  "意大利",
  "西班牙",
  "澳大利亚",
  "新西兰",
  "俄罗斯",
  "其他国家/地区",
];
const CERTIFICATE_TYPE_OPTIONS = ["身份证", "护照"];
const OTHER_COUNTRY_REGION_VALUE = "__other_country_region__";

function createAuthor() {
  return {
    name: "",
    countryRegion: "中国大陆",
    countryRegionCustom: "",
    certificateType: "身份证",
    idCard: "",
    email: "",
    address: "",
    phone: "",
    wechat: "",
    photo: "",
  };
}

function createTeamMember() {
  return { name: "", certificateType: "身份证", idCard: "", email: "", photo: "" };
}

function createParticipantProfile() {
  return {
    identity: "student",
    schoolNameCustom: "",
    educationLevel: "",
    companyName: "",
    industryField: "",
    industryFieldCustom: "",
  };
}

function createSeriesOverview(unit = WORK_DIMENSION_UNIT) {
  return { length: null, width: null, height: null, unit, images: "" };
}

function createSeriesItem(unit = WORK_DIMENSION_UNIT) {
  return { count: 1, length: null, width: null, height: null, unit, weight: null, images: "" };
}

function createDefaultForm(defaultDisplayMode) {
  return {
    plateId: undefined,
    categoryId: undefined,
    schoolId: undefined,
    participantProfile: createParticipantProfile(),
    creatorType: "personal",
    mentors: ["", "", ""],
    author: createAuthor(),
    teamMembers: [],
      work: {
        name: "",
        material: "",
        needElectricity: 0,
        powerSupplyType: "",
        displayMode: defaultDisplayMode,
      displayModeCustom: "",
      modelCount: 1,
      workType: "independent",
      count: 1,
      length: null,
      width: null,
      height: null,
      unit: WORK_DIMENSION_UNIT,
      weight: null,
      images: "",
      commitmentFile: "",
      description: "",
      overall: createSeriesOverview(),
      seriesItems: [createSeriesItem()],
    },
  };
}

function parseExtraJsonSafely(value) {
  if (!value) return {};
  try {
    return typeof value === "string" ? JSON.parse(value) : value;
  } catch (error) {
    return {};
  }
}

function toNullableNumber(value) {
  if (value === "" || value === null || value === undefined) return null;
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export default {
  name: "ContestApply",
  components: { ImageUpload, WordUpload },
  data() {
    return {
      loaded: false,
      submitting: false,
      submitSuccess: false,
      editMode: false,
      editWorkId: 0,
      editWorkDetail: null,
      currentStep: 1,
      stepTitles: STEP_TITLES,
      contestId: "graduation-art",
      activityMeta: null,
      plateOptions: [],
      categoryOptions: [],
      schoolOptions: [],
      participantIdentityOptions: PARTICIPANT_IDENTITY_OPTIONS,
      educationLevelOptions: EDUCATION_LEVEL_OPTIONS,
      industryFieldOptions: INDUSTRY_FIELD_OPTIONS,
      countryRegionOptions: COUNTRY_REGION_OPTIONS,
      certificateTypeOptions: CERTIFICATE_TYPE_OPTIONS,
      otherOptionValue: OTHER_OPTION_VALUE,
      otherSchoolOptionValue: OTHER_SCHOOL_OPTION_VALUE,
      otherCountryRegionValue: OTHER_COUNTRY_REGION_VALUE,
      otherDisplayModeValue: OTHER_DISPLAY_MODE_VALUE,
      displayModeOptions: DISPLAY_MODE_OPTIONS,
      powerSupplyOptions: POWER_SUPPLY_OPTIONS,
      maxWorkDescriptionLength: MAX_WORK_DESCRIPTION_LENGTH,
      form: createDefaultForm(DISPLAY_MODE_OPTIONS[0].value),
      draftSyncEnabled: false,
      draftSaveTimer: null,
    };
  },
  computed: {
    contestTitle() {
      if (this.activityMeta && this.activityMeta.title) {
        return this.editMode ? `${this.activityMeta.title}作品修改` : `${this.activityMeta.title}报名界面`;
      }
      return this.getContestKey() === "cashmere-design" ? "羊绒大赛立即报名" : "毕业季大赛立即报名";
    },
    submitButtonText() {
      return this.editMode ? "保存修改" : "提交报名";
    },
    descriptionLength() {
      return String(this.form.work.description || "").trim().length;
    },
    currentActivityId() {
      return Number((this.activityMeta && this.activityMeta.id) || resolveContestActivityId(this.contestId) || 0);
    },
    currentCommitmentTemplateUrl() {
      return resolveCommitmentTemplateUrl(this.activityMeta);
    },
    currentCommitmentTemplateVersion() {
      return (this.activityMeta && this.activityMeta.updateTime) || Date.now();
    },
    showCustomHeroBanner() {
      return [DEFAULT_GRADUATION_ACTIVITY_ID, 6].includes(this.currentActivityId);
    },
    heroBannerSrc() {
      return this.isGraduationContest ? graduationAssets.signupBanner : cashmereAssets.signupBanner;
    },
    isGraduationContest() {
      return this.currentActivityId === DEFAULT_GRADUATION_ACTIVITY_ID;
    },
    isStudentParticipant() {
      return this.isGraduationContest || this.form.participantProfile.identity !== "professional";
    },
    schoolSelectOptions() {
      if (this.isGraduationContest) {
        return this.schoolOptions;
      }
      return [...this.schoolOptions, { id: OTHER_SCHOOL_OPTION_VALUE, title: "其他" }];
    },
    isClothingProductPlate() {
      const target = this.plateOptions.find((item) => Number(item.id) === Number(this.form.plateId));
      const name = String((target && target.name) || "").trim();
      return /服装(类)?产品/.test(name);
    },
    availableDisplayModeOptions() {
      if (this.isClothingProductPlate) {
        return DISPLAY_MODE_OPTIONS.filter((item) => ["展台展示", "模特展示"].includes(item.value));
      }
      return DISPLAY_MODE_OPTIONS;
    },
    isClothingModelDisplay() {
      return this.isClothingProductPlate && this.form.work.displayMode === "模特展示";
    },
    countryRegionSelectOptions() {
      return this.countryRegionOptions.map((item) => ({
        value: item === "其他国家/地区" ? OTHER_COUNTRY_REGION_VALUE : item,
        label: item,
      }));
    },
  },
  mounted() {
    this.initPage();
  },
  beforeDestroy() {
    if (this.draftSaveTimer) {
      clearTimeout(this.draftSaveTimer);
      this.draftSaveTimer = null;
    }
  },
  watch: {
    "$route.fullPath"() {
      this.initPage();
    },
    form: {
      deep: true,
      handler() {
        this.scheduleDraftSave();
      },
    },
    currentStep() {
      this.scheduleDraftSave();
    },
    "form.work.needElectricity"(value) {
      if (Number(value) !== 1) this.form.work.powerSupplyType = "";
    },
    "form.work.workType"(value) {
      if (value === "series") this.syncSeriesCount();
      else this.form.work.count = 1;
    },
    "form.work.seriesItems": {
      deep: true,
      handler() {
        if (this.form.work.workType === "series") {
          this.syncSeriesCount();
        }
      },
    },
    "form.schoolId"(value) {
      if (value !== OTHER_SCHOOL_OPTION_VALUE) {
        this.form.participantProfile.schoolNameCustom = "";
      }
    },
    "form.author.countryRegion"(value) {
      if (value !== OTHER_COUNTRY_REGION_VALUE) {
        this.form.author.countryRegionCustom = "";
      }
    },
    "form.plateId"() {
      const values = this.availableDisplayModeOptions.map((item) => item.value);
      if (!values.includes(this.form.work.displayMode)) {
        this.form.work.displayMode = values[0] || DISPLAY_MODE_OPTIONS[0].value;
        this.form.work.displayModeCustom = "";
      }
    },
    "form.work.displayMode"(value) {
      if (value !== OTHER_DISPLAY_MODE_VALUE) {
        this.form.work.displayModeCustom = "";
      }
      if (value !== "模特展示") {
        this.form.work.modelCount = 1;
      }
    },
  },
  methods: {
    formatDate(value) {
      return value ? String(value).slice(0, 10) : "--";
    },
    extractList(res) {
      if (!res) return [];
      if (Array.isArray(res.data)) return res.data;
      if (Array.isArray(res.list)) return res.list;
      if (Array.isArray(res.content)) return res.content;
      if (res.data && Array.isArray(res.data.list)) return res.data.list;
      if (res.data && Array.isArray(res.data.content)) return res.data.content;
      return [];
    },
    getContestKey() {
      return getContestKey(this.contestId);
    },
    goToUserWorks() {
      const query = { id: 6 };
      if (this.activityMeta && this.activityMeta.id) {
        query.activityId = String(this.activityMeta.id);
      }
      this.$router.replace({ name: "UserCenter", query });
    },
    getContestHomePath() {
      return this.getContestKey() === "cashmere-design"
        ? "/ContestInfo/cashmere-design"
        : "/Graduation";
    },
    goBackToContest() {
      this.$router.push(this.getContestHomePath());
    },
    validateIdCard(value) {
      const result = {
        valid: false,
        code: -1,
        msg: "",
        info: {},
      };
      if (typeof value !== "string") {
        result.msg = "身份证必须为字符串";
        return result;
      }
      const rawText = value.trim().toUpperCase();
      const length = rawText.length;
      if (![15, 18].includes(length)) {
        result.code = 1;
        result.msg = `长度错误，必须15或18位，当前${length}位`;
        return result;
      }
      const pattern = length === 18 ? /^\d{17}[\dX]$/ : /^\d{15}$/;
      if (!pattern.test(rawText)) {
        result.code = 2;
        result.msg = "包含非法字符，18位末位可为X，其余必须为数字";
        return result;
      }
      const weights = [7, 9, 10, 5, 8, 4, 2, 1, 6, 3, 7, 9, 10, 5, 8, 4, 2];
      const checkCodes = ["1", "0", "X", "9", "8", "7", "6", "5", "4", "3", "2"];
      let id18 = rawText;
      if (length === 15) {
        const id17 = `${rawText.slice(0, 6)}19${rawText.slice(6)}`;
        const total = id17
          .split("")
          .reduce((sum, char, index) => sum + Number(char) * weights[index], 0);
        id18 = `${id17}${checkCodes[total % 11]}`;
      }
      const provinceCode = id18.slice(0, 2);
      const validProvinces = new Set([
        "11", "12", "13", "14", "15", "21", "22", "23",
        "31", "32", "33", "34", "35", "36", "37",
        "41", "42", "43", "44", "45", "46",
        "50", "51", "52", "53", "54",
        "61", "62", "63", "64", "65",
        "81", "82",
      ]);
      if (!validProvinces.has(provinceCode)) {
        result.code = 3;
        result.msg = `非法省级地址码：${provinceCode}`;
        return result;
      }
      const birthStr = id18.slice(6, 14);
      const year = Number(birthStr.slice(0, 4));
      const month = Number(birthStr.slice(4, 6));
      const day = Number(birthStr.slice(6, 8));
      const birthDate = new Date(year, month - 1, day);
      const isValidDate = birthDate instanceof Date
        && !Number.isNaN(birthDate.getTime())
        && birthDate.getFullYear() === year
        && birthDate.getMonth() === month - 1
        && birthDate.getDate() === day;
      const now = new Date();
      if (!isValidDate) {
        result.code = 4;
        result.msg = `出生日期非法：${birthStr}`;
        return result;
      }
      if (year < 1900 || birthDate.getTime() > now.getTime()) {
        result.code = 4;
        result.msg = `出生日期超出合理范围：${birthStr}`;
        return result;
      }
      const total = id18
        .slice(0, 17)
        .split("")
        .reduce((sum, char, index) => sum + Number(char) * weights[index], 0);
      const realCheckCode = checkCodes[total % 11];
      if (id18[17] !== realCheckCode) {
        result.code = 5;
        result.msg = `校验码错误，应为${realCheckCode}，实际${id18[17]}`;
        return result;
      }
      const gender = Number(id18[16]) % 2 === 1 ? "男" : "女";
      const age = now.getFullYear() - birthDate.getFullYear()
        - ((now.getMonth() + 1 < month || ((now.getMonth() + 1 === month) && now.getDate() < day)) ? 1 : 0);
      result.valid = true;
      result.code = 0;
      result.msg = "身份证号码有效";
      result.info = {
        id18,
        province_code: provinceCode,
        birthday: birthStr,
        gender,
        age,
      };
      return result;
    },
    isValidIdCard(value) {
      return this.validateIdCard(value).valid;
    },
    isValidCertificateNumber(type, value) {
      const text = String(value || "").trim();
      if (!text) return false;
      if (type === "护照") return /^[A-Za-z0-9]{5,20}$/.test(text);
      return this.isValidIdCard(text);
    },
    isValidPhone(value) {
      return /^1[3-9]\d{9}$/.test(String(value || "").trim());
    },
    hasMinNameLength(value, min = 2) {
      return String(value || "").trim().length >= min;
    },
    normalizeParticipantIdentity(value) {
      return value === "professional" ? "professional" : "student";
    },
    normalizeEducationLevel(value) {
      const text = String(value || "").trim();
      if (!text) return "";
      if (this.educationLevelOptions.includes(text)) return text;
      if (text === "研究生") return "硕士研究生";
      return "";
    },
    splitPresetOrOther(value, options) {
      const text = String(value || "").trim();
      if (!text) return { option: "", custom: "" };
      return options.includes(text) ? { option: text, custom: "" } : { option: OTHER_OPTION_VALUE, custom: text };
    },
    resolvePresetOrOther(optionValue, customValue) {
      if (optionValue === OTHER_OPTION_VALUE) return String(customValue || "").trim();
      return String(optionValue || "").trim();
    },
    selectParticipantIdentity(value) {
      this.form.participantProfile.identity = this.normalizeParticipantIdentity(value);
    },
    filterSchoolOption(input, option) {
      const keyword = String(input || "").trim().toLowerCase();
      if (!keyword) return true;
      const text = String((option && option.componentOptions && option.componentOptions.children && option.componentOptions.children[0] && option.componentOptions.children[0].text) || "").toLowerCase();
      return text.includes(keyword);
    },
    getSelectedSchoolName() {
      if (this.form.schoolId === OTHER_SCHOOL_OPTION_VALUE) {
        return String(this.form.participantProfile.schoolNameCustom || "").trim();
      }
      const target = this.schoolOptions.find((item) => Number(item.id) === Number(this.form.schoolId));
      return target ? target.title : "";
    },
    getSelectedCountryRegion() {
      if (this.form.author.countryRegion === OTHER_COUNTRY_REGION_VALUE) {
        return String(this.form.author.countryRegionCustom || "").trim();
      }
      return String(this.form.author.countryRegion || "").trim();
    },
    isValidEmail(value) {
      return /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(String(value || "").trim());
    },
    isPositiveNumber(value) {
      const num = Number(value);
      return Number.isFinite(num) && num > 0;
    },
    splitUploadList(value) {
      return String(value || "").split(",").map((item) => item.trim()).filter(Boolean);
    },
    getDraftStorageKey() {
      const contestKey = this.getContestKey();
      const activityId = Number((this.activityMeta && this.activityMeta.id) || resolveContestActivityId(this.contestId) || 0);
      return `ykmk:contest-apply:draft:${contestKey}:${activityId || "default"}`;
    },
    normalizeDraftForm(rawForm) {
      const base = createDefaultForm(this.displayModeOptions[0].value);
      const source = rawForm && typeof rawForm === "object" ? rawForm : {};
      const workSource = source.work && typeof source.work === "object" ? source.work : {};
      const normalizedSeriesItems = Array.isArray(workSource.seriesItems) && workSource.seriesItems.length
        ? workSource.seriesItems.map((item) => ({ ...createSeriesItem(), ...item }))
        : [createSeriesItem()];
      return {
        ...base,
        ...source,
        participantProfile: {
          ...base.participantProfile,
          ...(source.participantProfile || {}),
        },
        mentors: Array.from({ length: 3 }, (_, index) =>
          String((Array.isArray(source.mentors) && source.mentors[index]) || "")
        ),
        author: {
          ...base.author,
          ...(source.author || {}),
        },
        teamMembers: Array.isArray(source.teamMembers)
          ? source.teamMembers.map((item) => ({ ...createTeamMember(), ...(item || {}) }))
          : [],
        work: {
          ...base.work,
          ...workSource,
          overall: {
            ...base.work.overall,
            ...(workSource.overall || {}),
          },
          seriesItems: normalizedSeriesItems,
        },
      };
    },
    saveDraftToStorage() {
      if (!this.draftSyncEnabled || this.editMode) {
        return;
      }
      const snapshot = {
        version: 1,
        savedAt: Date.now(),
        currentStep: this.currentStep,
        form: this.form,
      };
      localStorage.setItem(this.getDraftStorageKey(), JSON.stringify(snapshot));
    },
    scheduleDraftSave() {
      if (!this.draftSyncEnabled || this.editMode) {
        return;
      }
      if (this.draftSaveTimer) {
        clearTimeout(this.draftSaveTimer);
      }
      this.draftSaveTimer = setTimeout(() => {
        this.draftSaveTimer = null;
        this.saveDraftToStorage();
      }, 300);
    },
    clearDraftStorage() {
      if (this.draftSaveTimer) {
        clearTimeout(this.draftSaveTimer);
        this.draftSaveTimer = null;
      }
      localStorage.removeItem(this.getDraftStorageKey());
    },
    restoreDraftFromStorage() {
      if (this.editMode) {
        return;
      }
      const raw = localStorage.getItem(this.getDraftStorageKey());
      if (!raw) {
        return;
      }
      try {
        const snapshot = JSON.parse(raw);
        if (!snapshot || typeof snapshot !== "object" || !snapshot.form) {
          return;
        }
        this.form = this.normalizeDraftForm(snapshot.form);
        const restoredStep = Number(snapshot.currentStep || 1);
        this.currentStep = restoredStep >= 1 && restoredStep <= 3 ? restoredStep : 1;
        this.$message.success("已恢复上次未提交的报名信息");
      } catch (error) {
        localStorage.removeItem(this.getDraftStorageKey());
      }
    },
    getUploadCount(value) {
      return this.splitUploadList(value).length;
    },
    buildDimensionText(length, width, height, unit) {
      return `${length}${unit} x ${width}${unit} x ${height}${unit}`;
    },
    getSeriesWorkCount(seriesItems = this.form.work.seriesItems) {
      const total = (Array.isArray(seriesItems) ? seriesItems : []).reduce((sum, item) => {
        const count = Number(item && item.count);
        return sum + (Number.isInteger(count) && count > 0 ? count : 0);
      }, 0);
      return total > 0 ? total : 1;
    },
    collectIdCards() {
      const cards = [];
      if (this.form.author.idCard) cards.push(this.form.author.idCard.trim());
      this.form.teamMembers.forEach((item) => {
        if (item.idCard) cards.push(item.idCard.trim());
      });
      return cards;
    },
    addSeriesItem() {
      this.form.work.seriesItems.push(createSeriesItem(this.form.work.overall.unit || WORK_DIMENSION_UNIT));
      this.syncSeriesCount();
    },
    removeSeriesItem(index) {
      if (this.form.work.seriesItems.length <= 1) {
        this.$message.warning("系列作品至少保留 1 个单件。");
        return;
      }
      this.form.work.seriesItems.splice(index, 1);
      this.syncSeriesCount();
    },
    syncSeriesCount() {
      this.form.work.count = this.getSeriesWorkCount(this.form.work.seriesItems);
    },
    validateDimensionBlock(block, label) {
      if (!this.isPositiveNumber(block.length) || !this.isPositiveNumber(block.width) || !this.isPositiveNumber(block.height)) {
        return `请完整填写${label}的长、宽、高。`;
      }
      return "";
    },
    async initPage() {
      this.loaded = false;
      this.submitting = false;
      this.submitSuccess = false;
      this.draftSyncEnabled = false;
      if (this.draftSaveTimer) {
        clearTimeout(this.draftSaveTimer);
        this.draftSaveTimer = null;
      }
      this.currentStep = 1;
      this.contestId = String(this.$route.params.contestId || "graduation-art");
      this.editMode = this.$route.query.mode === "edit" || !!this.$route.query.workId;
      this.editWorkId = Number(this.$route.query.workId || 0);
      this.editWorkDetail = null;
      this.activityMeta = null;
      this.plateOptions = [];
      this.categoryOptions = [];
      this.schoolOptions = [];
      this.form = createDefaultForm(this.displayModeOptions[0].value);
      await this.loadActivityMeta();
      if (this.editMode) {
        await this.loadExistingWork();
      } else {
        this.restoreDraftFromStorage();
      }
      this.normalizeGraduationParticipant();
      if (this.redirectIfSignupClosed()) return;
      this.draftSyncEnabled = !this.editMode;
      this.loaded = true;
    },
    async loadActivityMeta() {
      try {
        const activityId = resolveContestActivityId(this.contestId) || DEFAULT_GRADUATION_ACTIVITY_ID;
        const detailRes = await activityDetail(activityId, { isShow: 0, uid: 0 });
        const target = detailRes && detailRes.data ? detailRes.data : null;
        if (!target) {
          this.activityMeta = null;
          return;
        }
        this.activityMeta = target;
        const [plateRes, categoryRes, schoolRes] = await Promise.all([
          target.plateVos && target.plateVos.length ? Promise.resolve({ data: target.plateVos }) : getActivityPlateList(activityId),
          target.categoryVos && target.categoryVos.length ? Promise.resolve({ data: target.categoryVos }) : getActivityCategoryList(activityId),
          target.schoolVos && target.schoolVos.length ? Promise.resolve({ data: target.schoolVos }) : getActivitySchoolList(activityId),
        ]);
        const normalizedOptions = normalizeVoteOptionGroups(this.extractList(plateRes), this.extractList(categoryRes));
        this.plateOptions = normalizedOptions.plateVos;
        this.categoryOptions = normalizedOptions.categoryVos;
        this.schoolOptions = this.extractList(schoolRes);
        if (!this.form.plateId && this.plateOptions.length) this.form.plateId = Number(this.plateOptions[0].id);
        if (!this.isGraduationContest && !this.form.categoryId && this.categoryOptions.length) {
          this.form.categoryId = Number(this.categoryOptions[0].id);
        }
      } catch (error) {
        this.activityMeta = null;
      }
    },
    normalizeGraduationParticipant() {
      if (!this.isGraduationContest) return;
      this.form.participantProfile.identity = "student";
      const selectedSchoolId = Number(this.form.schoolId);
      const hasBoundSchool = this.schoolOptions.some((item) => Number(item.id) === selectedSchoolId);
      if (!hasBoundSchool) {
        this.form.schoolId = undefined;
      }
      this.form.participantProfile.schoolNameCustom = "";
    },
    async loadExistingWork() {
      if (!this.editWorkId) {
        this.$message.error("未找到待修改的作品。");
        this.goToUserWorks();
        return;
      }
      try {
        const res = await userEditVoteWorks(this.editWorkId);
        const detail = res && res.data ? res.data : null;
        if (!detail) throw new Error("作品不存在");
        this.editWorkDetail = detail;
        this.applyExistingWork(detail);
      } catch (error) {
        this.$message.error((error && (error.msg || error.message)) || "作品详情加载失败");
        this.goToUserWorks();
      }
    },
    ensureSchoolOption(detail) {
      if (!detail || !detail.schoolId) return;
      const exists = this.schoolOptions.some((item) => Number(item.id) === Number(detail.schoolId));
      if (!exists) this.schoolOptions.push({ id: Number(detail.schoolId), title: detail.schoolName || "已保存院校" });
    },
    applyExistingWork(detail) {
      const extra = parseExtraJsonSafely(detail.extraJson);
      const author = extra.author || {};
      const work = extra.work || {};
      const teamMembers = Array.isArray(extra.teamMembers) ? extra.teamMembers : [];
      const participantProfile = extra.participantProfile || extra.participant || {};
      const seriesItems = Array.isArray(work.seriesItems) ? work.seriesItems : [];
      const isSeriesWork = work.workType === "series" || seriesItems.length > 0;
      const identity = this.normalizeParticipantIdentity(
        participantProfile.identity || participantProfile.participantIdentity || (
          detail.schoolId != null && Number(detail.schoolId) > 0 ? "student" :
            detail.education === "非学生/在职人员" ? "professional" : "student"
        )
      );
      const industryFieldValue = participantProfile.industryField || detail.speciality || "";
      const industrySelection = this.splitPresetOrOther(industryFieldValue, this.industryFieldOptions);
      this.ensureSchoolOption(detail);
      this.form.plateId = detail.plateId != null ? Number(detail.plateId) : this.form.plateId;
      this.form.categoryId = detail.categoryId != null ? Number(detail.categoryId) : this.form.categoryId;
      const savedSchoolName = participantProfile.schoolName || detail.schoolName || "";
      const hasSavedSchoolId = detail.schoolId != null && Number(detail.schoolId) > 0;
      this.form.schoolId = identity === "student"
        ? (hasSavedSchoolId ? Number(detail.schoolId) : (savedSchoolName ? OTHER_SCHOOL_OPTION_VALUE : undefined))
        : undefined;
      this.form.participantProfile = {
        identity,
        schoolNameCustom: identity === "student" && !hasSavedSchoolId ? String(savedSchoolName || "").trim() : "",
        educationLevel: identity === "student" ? this.normalizeEducationLevel(participantProfile.educationLevel || detail.education) : "",
        companyName: identity === "professional" ? (participantProfile.companyName || participantProfile.company || "") : "",
        industryField: identity === "professional" ? industrySelection.option : "",
        industryFieldCustom: identity === "professional" ? industrySelection.custom : "",
      };
      this.form.creatorType = Number(detail.creatorType) === 1 || extra.creatorType === "team" ? "team" : "personal";
      this.form.mentors = identity === "student"
        ? [detail.mentor || "", detail.mentorSecond || "", detail.mentorThird || ""]
        : ["", "", ""];
      this.form.author = {
        name: author.name || detail.realName || "",
        countryRegion: author.countryRegion
          ? (this.countryRegionOptions.includes(author.countryRegion) && author.countryRegion !== "其他国家/地区"
            ? author.countryRegion
            : OTHER_COUNTRY_REGION_VALUE)
          : "中国大陆",
        countryRegionCustom: author.countryRegion && !this.countryRegionOptions.includes(author.countryRegion)
          ? author.countryRegion
          : String(author.countryRegionCustom || "").trim(),
        certificateType: author.certificateType || "身份证",
        idCard: author.idCard || detail.idCard || "",
        email: author.email || detail.email || "",
        address: author.address || detail.address || "",
        phone: author.phone || detail.mobile || "",
        wechat: author.wechat || "",
        photo: author.photo || detail.avatar || "",
      };
      this.form.teamMembers = teamMembers.map((item) => ({ ...createTeamMember(), ...item, certificateType: item.certificateType || "身份证" }));
      this.form.work.name = work.name || detail.name || "";
      this.form.work.material = work.material || detail.material || "";
      this.form.work.needElectricity = work.needElectricity != null ? Number(work.needElectricity) : Number(detail.isElectricity || 0);
      this.form.work.powerSupplyType = work.powerSupplyType || detail.powerSupplyType || "";
      if (this.form.work.needElectricity !== 1 && this.form.work.powerSupplyType) {
        this.form.work.needElectricity = 1;
      }
      const incomingDisplayMode = this.normalizeDisplayModeOption(
        work.displayMode || detail.displayMode || this.displayModeOptions[0].value
      );
      if (this.displayModeOptions.some((item) => item.value === incomingDisplayMode)) {
        this.form.work.displayMode = incomingDisplayMode;
        this.form.work.displayModeCustom = String(work.displayModeCustom || "").trim();
      } else {
        this.form.work.displayMode = OTHER_DISPLAY_MODE_VALUE;
        this.form.work.displayModeCustom = String(incomingDisplayMode || work.displayModeCustom || "").trim();
      }
      this.form.work.modelCount = Number(work.modelCount || 1);
      this.form.work.workType = isSeriesWork ? "series" : "independent";
      this.form.work.count = Number(work.count || detail.workCount || 1);
      this.form.work.commitmentFile = work.commitmentFile || detail.commitmentFile || "";
      this.form.work.description = work.description || detail.content || "";
      if (isSeriesWork) {
        const overall = work.overall || {};
        this.form.work.overall = {
          ...createSeriesOverview(overall.unit || detail.workUnit || WORK_DIMENSION_UNIT),
          length: toNullableNumber(overall.length != null ? overall.length : detail.workLength),
          width: toNullableNumber(overall.width != null ? overall.width : detail.workWidth),
          height: toNullableNumber(overall.height != null ? overall.height : detail.workHeight),
          unit: overall.unit || detail.workUnit || WORK_DIMENSION_UNIT,
          images: overall.images || detail.image || "",
        };
        this.form.work.seriesItems = (seriesItems.length ? seriesItems : [createSeriesItem(detail.workUnit || WORK_DIMENSION_UNIT)]).map((item) => ({
          ...createSeriesItem(item.unit || detail.workUnit || WORK_DIMENSION_UNIT),
          ...item,
          count: Number(item.count != null ? item.count : ((seriesItems.length ? 1 : Number(detail.workCount || 1)))),
          length: toNullableNumber(item.length),
          width: toNullableNumber(item.width),
          height: toNullableNumber(item.height),
          weight: toNullableNumber(item.weight),
        }));
        this.syncSeriesCount();
      } else {
        this.form.work.length = toNullableNumber(work.length != null ? work.length : detail.workLength);
        this.form.work.width = toNullableNumber(work.width != null ? work.width : detail.workWidth);
        this.form.work.height = toNullableNumber(work.height != null ? work.height : detail.workHeight);
        this.form.work.unit = work.unit || detail.workUnit || WORK_DIMENSION_UNIT;
        this.form.work.weight = toNullableNumber(work.weight != null ? work.weight : detail.weight);
        this.form.work.images = work.images || detail.image || "";
      }
    },
    resolveContestStage() {
      return resolveContestTimeline(this.activityMeta || {});
    },
    redirectIfSignupClosed() {
      if (!this.activityMeta || !this.activityMeta.id) return false;
      const stage = this.resolveContestStage();
      if (stage.key === "signup") return false;
      this.$warning({
        title: this.editMode ? "当前赛事已截止修改" : "当前赛事已停止报名",
        content: resolveSignupBlockMessage(this.activityMeta),
        onOk: () => this.$router.replace({ name: "UserCenter", query: { id: this.editMode ? 6 : 9 } }),
      });
      return true;
    },
    addTeamMember() {
      if (this.form.teamMembers.length >= 3) {
        this.$message.warning("团队成员最多可添加 3 位。");
        return;
      }
      this.form.teamMembers.push(createTeamMember());
    },
    removeTeamMember(index) {
      this.form.teamMembers.splice(index, 1);
    },
    validateStep(step) {
      if (step === 1) {
        if (!this.form.plateId) return "请选择参赛板块。";
        if (this.isGraduationContest && !this.form.categoryId) return "请选择参赛门类。";
        return "";
      }
      if (step === 2) {
        if (this.isStudentParticipant) {
          if (!this.form.schoolId) return "请选择所在院校。";
          if (!this.isGraduationContest && this.form.schoolId === OTHER_SCHOOL_OPTION_VALUE && !String(this.form.participantProfile.schoolNameCustom || "").trim()) {
            return "请填写其他院校名称。";
          }
          if (!this.form.participantProfile.educationLevel) return "请选择学历层次。";
          if (this.form.mentors.some((mentor) => mentor && !this.hasMinNameLength(mentor))) {
            return "指导老师姓名至少需填写 2 个字。";
          }
        } else {
          if (!this.form.participantProfile.industryField) return "请选择从业方向/领域。";
          if (this.form.participantProfile.industryField === OTHER_OPTION_VALUE && !this.form.participantProfile.industryFieldCustom) {
            return "请填写具体从业方向/领域。";
          }
        }
        const author = this.form.author;
        if (!author.name) return "请填写作者姓名。";
        if (!author.countryRegion) return "请选择国家-地区。";
        if (author.countryRegion === OTHER_COUNTRY_REGION_VALUE && !String(author.countryRegionCustom || "").trim()) {
          return "请填写其他国家-地区。";
        }
        if (!author.certificateType) return "请选择证件类型。";
        if (!author.idCard) return "请填写作者证件号。";
        if (!this.isValidCertificateNumber(author.certificateType, author.idCard)) {
          if (author.certificateType === "护照") {
            return "请输入正确的作者护照号。";
          }
          return "作者身份证信息错误。";
        }
        if (!author.email || !this.isValidEmail(author.email)) return "请输入正确的作者邮箱。";
        if (!author.address) return "请填写作品寄回地址。";
        if (String(author.address || "").trim().length < 6) return "作品寄回地址至少需填写 6 个字。";
        if (!author.phone || !this.isValidPhone(author.phone)) return "请输入正确的作者手机号。";
        if (!author.photo) return "请上传作者照片。";
        const idCards = this.collectIdCards();
        if (new Set(idCards).size !== idCards.length) return "作者与团队成员证件号不能重复。";
        if (this.form.creatorType === "team") {
          if (!this.form.teamMembers.length) return "团队参赛请至少填写 1 位团队成员。";
          for (let i = 0; i < this.form.teamMembers.length; i += 1) {
            const member = this.form.teamMembers[i];
            if (!member.name) return `请填写团队成员 ${i + 1} 的姓名。`;
            if (!member.certificateType) return `请选择团队成员 ${i + 1} 的证件类型。`;
            if (!member.idCard) return `请填写团队成员 ${i + 1} 的证件号。`;
            if (!this.isValidCertificateNumber(member.certificateType, member.idCard)) {
              if (member.certificateType === "护照") {
                return `请输入正确的团队成员 ${i + 1} 护照号。`;
              }
              return `团队成员 ${i + 1} 身份证信息错误。`;
            }
            if (!member.email || !this.isValidEmail(member.email)) return `请输入正确的团队成员 ${i + 1} 邮箱。`;
            if (!member.photo) return `请上传团队成员 ${i + 1} 的照片。`;
          }
        }
        return "";
      }
      if (step === 3) {
        const work = this.form.work;
        if (!work.name) return "请填写作品名称。";
        if (!work.material) return "请填写作品材质。";
        if (Number(work.needElectricity) === 1 && !work.powerSupplyType) return "请选择供电方式。";
        if (!work.displayMode) return "请选择展示方式。";
        if (work.displayMode === OTHER_DISPLAY_MODE_VALUE && !String(work.displayModeCustom || "").trim()) {
          return "请填写其他展示方式内容。";
        }
        if (work.workType === "series") {
          if (!work.seriesItems.length) return "请至少填写 1 个系列单件。";
          const overallMsg = this.validateDimensionBlock(work.overall, "系列作品总览");
          if (overallMsg) return overallMsg;
          const overallImageCount = this.getUploadCount(work.overall.images);
          if (overallImageCount < 3) return "请至少上传 3 张系列总览图片。";
          if (overallImageCount > 10) return "系列总览图片最多可上传 10 张。";
          for (let i = 0; i < work.seriesItems.length; i += 1) {
            const item = work.seriesItems[i];
            if (!Number.isInteger(Number(item.count)) || Number(item.count) <= 0) {
              return `请填写单件 ${i + 1} 的数量。`;
            }
            const itemMsg = this.validateDimensionBlock(item, `单件 ${i + 1}`);
            if (itemMsg) return itemMsg;
            if (!this.isPositiveNumber(item.weight)) return `请填写单件 ${i + 1} 的重量。`;
            const itemImageCount = this.getUploadCount(item.images);
            if (itemImageCount < 3) return `请至少上传单件 ${i + 1} 的 3 张图片。`;
            if (itemImageCount > 10) return `单件 ${i + 1} 的图片最多可上传 10 张。`;
          }
          this.syncSeriesCount();
        } else {
          work.count = 1;
          if (this.isClothingModelDisplay) {
            if (!Number.isInteger(Number(work.modelCount)) || Number(work.modelCount) <= 0) {
              return "请填写需要的模特数量。";
            }
          } else {
            const sizeMsg = this.validateDimensionBlock(work, "作品");
            if (sizeMsg) return sizeMsg;
            if (!this.isPositiveNumber(work.weight)) return "请填写作品重量。";
          }
          const workImageCount = this.getUploadCount(work.images);
          if (workImageCount < 3) return "请至少上传 3 张作品图片。";
          if (workImageCount > 10) return "作品图片最多可上传 10 张。";
        }
        if (!work.commitmentFile) return "请上传原创承诺书。";
        if (!work.description) return "请填写作品说明。";
        if (String(work.description || "").trim().length > MAX_WORK_DESCRIPTION_LENGTH) return `作品说明不能超过 ${MAX_WORK_DESCRIPTION_LENGTH} 字。`;
      }
      return "";
    },
    async nextStep() {
      const msg = this.validateStep(this.currentStep);
      if (msg) return this.$message.warning(msg);
      if (this.currentStep < 3) this.currentStep += 1;
    },
    prevStep() {
      if (this.currentStep > 1) this.currentStep -= 1;
    },
    mapDisplayModeToPanoramaType(mode) {
      const target = this.displayModeOptions.find((item) => item.value === mode);
      return target ? target.panoramaType : 0;
    },
    normalizeDisplayModeOption(mode) {
      const value = String(mode || "").trim();
      if (!value) return this.displayModeOptions[0].value;
      const aliasMap = {
        平面展示: "展台展示",
        实物陈列: "展台展示",
        动态多媒体: "墙面展示",
        数字交互: "墙面展示",
        综合展示: "展台展示",
        悬挂: "悬吊悬挂",
        易吊悬挂: "悬吊悬挂",
      };
      return aliasMap[value] || value;
    },
    resolveDisplayModeValue() {
      if (this.form.work.displayMode === OTHER_DISPLAY_MODE_VALUE) {
        return String(this.form.work.displayModeCustom || "").trim();
      }
      return this.form.work.displayMode;
    },
    async downloadCommitmentTemplate() {
      try {
        let templateUrl = this.currentCommitmentTemplateUrl;
        let templateVersion = this.currentCommitmentTemplateVersion;
        if (this.currentActivityId) {
          const detailRes = await activityDetail(this.currentActivityId, { isShow: 0, uid: 0 });
          const latestActivity = detailRes && detailRes.data ? detailRes.data : null;
          if (latestActivity) {
            templateUrl = resolveCommitmentTemplateUrl(latestActivity);
            templateVersion = latestActivity.updateTime || Date.now();
            this.activityMeta = {
              ...this.activityMeta,
              ...latestActivity
            };
          }
        }
        await downloadFileByUrl(templateUrl, "原创承诺书", templateVersion);
      } catch (error) {
        this.$message.error((error && error.message) || "原创承诺书模板下载失败");
      }
    },
    resolveCategoryId() {
      if (this.form.categoryId !== undefined && this.form.categoryId !== null && this.form.categoryId !== "") {
        return Number(this.form.categoryId);
      }
      if (this.categoryOptions.length) {
        return Number(this.categoryOptions[0].id);
      }
      return null;
    },
    buildPayload() {
      const activityId = Number((this.activityMeta && this.activityMeta.id) || 0);
      if (!activityId) throw new Error("赛事信息加载失败，请刷新后重试。");
      const categoryId = this.resolveCategoryId();
      const isStudent = this.isStudentParticipant;
      const participantProfile = this.form.participantProfile;
      const speciality = isStudent
        ? ""
        : this.resolvePresetOrOther(participantProfile.industryField, participantProfile.industryFieldCustom);
      const education = isStudent ? participantProfile.educationLevel : "非学生/在职人员";
      const customSchoolName = String(participantProfile.schoolNameCustom || "").trim();
      const isOtherSchool = isStudent && this.form.schoolId === OTHER_SCHOOL_OPTION_VALUE;
      const schoolId = isStudent && this.form.schoolId && !isOtherSchool ? Number(this.form.schoolId) : null;
      const schoolName = isStudent ? (isOtherSchool ? customSchoolName : this.getSelectedSchoolName()) : "";
      const mentors = isStudent ? this.form.mentors.map((item) => item.trim()).filter(Boolean) : [];
      const work = this.form.work;
      const author = this.form.author;
      const displayModeValue = this.resolveDisplayModeValue();
      const hasElectricity = Number(work.needElectricity) === 1 ? 1 : 0;
      const isSeriesWork = work.workType === "series";
      const modelCount = this.isClothingModelDisplay ? Number(work.modelCount || 0) : null;
      const overall = isSeriesWork ? {
        length: Number(work.overall.length), width: Number(work.overall.width), height: Number(work.overall.height), unit: WORK_DIMENSION_UNIT,
        images: work.overall.images, imageList: this.splitUploadList(work.overall.images),
      } : null;
      const seriesItems = isSeriesWork ? work.seriesItems.map((item, index) => ({
        sort: index + 1, count: Number(item.count || 1), length: Number(item.length), width: Number(item.width), height: Number(item.height), unit: WORK_DIMENSION_UNIT,
        weight: Number(item.weight), images: item.images, imageList: this.splitUploadList(item.images),
      })) : [];
      const workCount = isSeriesWork ? this.getSeriesWorkCount(seriesItems) : 1;
      const summaryLength = isSeriesWork ? overall.length : (this.isClothingModelDisplay ? null : Number(work.length));
      const summaryWidth = isSeriesWork ? overall.width : (this.isClothingModelDisplay ? null : Number(work.width));
      const summaryHeight = isSeriesWork ? overall.height : (this.isClothingModelDisplay ? null : Number(work.height));
      const summaryImages = isSeriesWork ? overall.images : work.images;
      const summaryWeight = isSeriesWork || this.isClothingModelDisplay ? "" : String(work.weight);
      const countryRegion = this.getSelectedCountryRegion();
      const extraJson = {
        creatorType: this.form.creatorType,
        creatorTypeLabel: this.form.creatorType === "team" ? "团队" : "个人",
        participantProfile: {
          identity: isStudent ? "student" : "professional",
          identityLabel: isStudent ? "学生" : "非学生/在职人员",
          schoolId,
          schoolName,
          schoolOption: isStudent ? this.form.schoolId : "",
          schoolNameCustom: isStudent ? customSchoolName : "",
          major: "",
          majorOption: "",
          majorCustom: "",
          educationLevel: isStudent ? participantProfile.educationLevel : "",
          companyName: isStudent ? "" : String(participantProfile.companyName || "").trim(),
          industryField: isStudent ? "" : speciality,
          industryFieldOption: isStudent ? "" : participantProfile.industryField,
          industryFieldCustom: isStudent ? "" : String(participantProfile.industryFieldCustom || "").trim(),
        },
        author: {
          ...author,
          countryRegion,
          countryRegionOption: author.countryRegion,
          countryRegionCustom: String(author.countryRegionCustom || "").trim(),
          certificateType: author.certificateType || "身份证",
          certificateNo: author.idCard ? author.idCard.trim() : "",
          wechat: String(author.wechat || "").trim(),
          address: author.address ? author.address.trim() : "",
        },
        teamMembers: this.form.teamMembers.map((item) => ({
          ...item,
          certificateType: item.certificateType || "身份证",
          certificateNo: item.idCard ? item.idCard.trim() : "",
          idCard: item.idCard ? item.idCard.trim() : "",
          name: item.name ? item.name.trim() : "",
          email: item.email ? item.email.trim() : "",
        })),
        work: {
          name: work.name.trim(), material: String(work.material || "").trim(), needElectricity: hasElectricity, powerSupplyType: work.powerSupplyType || "",
          displayMode: displayModeValue, displayModeOption: work.displayMode, displayModeCustom: String(work.displayModeCustom || "").trim(),
          workType: work.workType, count: workCount,
          modelCount,
          length: summaryLength, width: summaryWidth, height: summaryHeight, unit: WORK_DIMENSION_UNIT,
          weight: isSeriesWork || this.isClothingModelDisplay ? null : Number(work.weight), images: summaryImages, imageList: this.splitUploadList(summaryImages),
          commitmentFile: work.commitmentFile, description: work.description.trim(), overall, seriesItems,
        },
      };
      const worksSizeText = this.isClothingModelDisplay
        ? `模特数量：${modelCount || 0}`
        : this.buildDimensionText(summaryLength, summaryWidth, summaryHeight, WORK_DIMENSION_UNIT);
      const payload = {
        activityId, plateId: Number(this.form.plateId), categoryId, schoolId,
        compete: 0, realName: author.name.trim(), avatar: author.photo || "", sex: 0, mobile: author.phone.trim(),
        email: author.email.trim(), address: author.address.trim(), creatorType: this.form.creatorType === "team" ? 1 : 0,
        idCard: author.idCard.trim(), mentor: mentors[0] || "", mentorSecond: mentors[1] || "", mentorThird: mentors[2] || "",
        name: work.name.trim(), content: work.description.trim(), material: String(work.material || "").trim(),
        weight: summaryWeight, workCount, worksSize: worksSizeText,
        workLength: summaryLength, workWidth: summaryWidth, workHeight: summaryHeight, workUnit: WORK_DIMENSION_UNIT, image: summaryImages, video: "",
        commitmentFile: work.commitmentFile, panorama: "", isShow: 1, speciality,
        education, isElectricity: hasElectricity, powerSupplyType: work.powerSupplyType || "",
        panoramaType: this.mapDisplayModeToPanoramaType(work.displayMode), displayMode: displayModeValue, extraJson: JSON.stringify(extraJson),
      };
      if (this.editMode && this.editWorkId) payload.id = Number(this.editWorkId);
      return payload;
    },
    async submit() {
      if (this.resolveContestStage().key !== "signup") {
        this.$message.warning(resolveSignupBlockMessage(this.activityMeta));
        this.$router.replace({ name: "UserCenter", query: { id: this.editMode ? 6 : 9 } });
        return;
      }
      const msg = this.validateStep(1) || this.validateStep(2) || this.validateStep(3);
      if (msg) return this.$message.warning(msg);
      this.submitting = true;
      try {
        const payload = this.buildPayload();
        if (this.editMode) await worksSignUpEdit(payload);
        else await worksSignUp(payload);
        this.clearDraftStorage();
        this.$message.success(this.editMode ? "作品修改成功" : "报名提交成功可前往我的-参赛作品中修改作品信息");
        this.submitSuccess = true;
      } catch (error) {
        const message = (error && (error.msg || error.message)) || (this.editMode ? "作品修改失败" : "报名提交失败");
        this.$message.error(message);
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.contest-apply {
  width: min(1120px, calc(100% - 32px)); margin: 24px auto 40px;
  .page-top,.form-card,.result-card,.state-card { border-radius: 24px; box-shadow: 0 14px 36px rgba(177,130,80,.08); }
  .page-top { margin-bottom: 20px; padding: 28px 24px; background: linear-gradient(90deg,#a78fd5 0%,#f0b5b5 56%,#f7e8a4 100%); }
  .page-top-banner { padding: 0; background: transparent; overflow: hidden; }
  .hero-banner-image { display:block; width:100%; height:auto; border-radius:24px; }
  .hero-title { text-align: center; font-size: 38px; line-height: 1.45; font-weight: 700; color: #fff; }
  .hero-meta,.result-actions,.label-row,.inline-controls { display:flex; gap:12px; align-items:center; }
  .hero-meta { justify-content:center; margin-top:12px; color:rgba(255,255,255,.95); font-size:15px; }
  .result-actions { justify-content:center; margin-top:24px; }
  .meta-tag { padding:4px 10px; border-radius:999px; background:rgba(255,255,255,.22); }
  .state-card,.result-card,.form-card { background:#fffaf4; }
  .state-card { padding:16px; } .result-card { padding:56px 32px; text-align:center; }
  .result-title { font-size:30px; font-weight:700; color:#2f2b24; } .result-desc { margin-top:12px; color:#8c7b69; }
  .result-tip { margin-top:10px; color:#9b6a44; font-size:15px; }
  .form-card { padding:28px; } .step-bar { display:grid; grid-template-columns:repeat(3,1fr); gap:12px; margin-bottom:28px; background:#f7eee3; border-radius:18px; padding:14px 16px; }
  .step-item { display:flex; align-items:center; justify-content:center; gap:10px; color:#bba28f; font-weight:500; }
  .step-index { width:24px; height:24px; border-radius:50%; background:#d8cdc1; color:#fff; line-height:24px; text-align:center; font-size:13px; }
  .step-item.active { color:#ef8f42; } .step-item.active .step-index { background:#ef8f42; }
  .form-section { margin-bottom:28px; padding:22px 24px; background:#fff; border-radius:20px; border:1px solid #f1e6d8; }
  .section-title { margin-bottom:16px; font-size:22px; font-weight:700; color:#2f2b24; position:relative; padding-left:14px; }
  .section-title::before { content:""; position:absolute; left:0; top:5px; width:4px; height:24px; border-radius:999px; background:linear-gradient(180deg,#8d7ad3 0%,#ef8f42 100%); }
  .with-action { display:flex; align-items:center; justify-content:space-between; gap:16px; }
  .with-tip { display:flex; align-items:center; gap:14px; flex-wrap:wrap; }
  .title-inline-group { display:flex; align-items:center; gap:14px; flex-wrap:wrap; min-width:0; }
  .section-tip,.upload-tip { color:#8f7f6f; font-size:13px; }
  .template-download-link { padding: 0; height: auto; }
  .inline-tip { margin-bottom:0; font-weight:400; line-height:1.4; }
  .series-title { margin-top:8px; } .option-row { display:flex; flex-wrap:wrap; gap:12px; }
  .graduation-category-row {
    display:grid;
    grid-template-columns:repeat(9,minmax(0,1fr));
    gap:10px;
  }
  .graduation-category-row .option-pill {
    min-width:0;
    padding:12px 8px;
    white-space:nowrap;
  }
  .identity-card-row { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
  .identity-card {
    padding:18px 20px; border-radius:18px; border:1px solid #e9dccd; background:#fffaf4; cursor:pointer;
    transition:all .2s ease; box-shadow:0 8px 18px rgba(177,130,80,.04);
  }
  .identity-card:hover { transform:translateY(-1px); border-color:#efc39b; }
  .identity-card.selected {
    border-color:#ef8f42; background:linear-gradient(135deg,rgba(239,143,66,.12),rgba(141,122,211,.08));
    box-shadow:0 12px 24px rgba(239,143,66,.12);
  }
  .identity-card-title { font-size:18px; font-weight:700; color:#2f2b24; }
  .identity-card-desc { margin-top:8px; color:#8f7f6f; font-size:14px; line-height:1.6; }
  .option-pill { min-width:128px; padding:12px 18px; text-align:center; border-radius:999px; border:1px solid #e9dccd; color:#7d6c5d; cursor:pointer; background:#fffaf4; }
  .option-pill.selected { color:#fff; border-color:#ef8f42; background:linear-gradient(90deg,#f3a15d 0%,#ef8f42 100%); box-shadow:0 10px 22px rgba(239,143,66,.24); }
  .grid { display:grid; gap:18px 20px; } .grid.two-col { grid-template-columns:repeat(2,minmax(0,1fr)); } .grid.three-col { grid-template-columns:repeat(3,minmax(0,1fr)); } .grid.four-col { grid-template-columns:repeat(4,minmax(0,1fr)); }
  .field { display:flex; flex-direction:column; gap:8px; } .field label { color:#4a3f35; font-weight:600; } .field.wide,.upload-field.wide { grid-column:1 / -1; }
  .mentor-tip { color:#8f7f6f; font-size:13px; }
  .inline-block { margin-top:18px; display:flex; flex-direction:column; gap:10px; } .inline-block label { color:#4a3f35; font-weight:600; }
  .power-supply-group,.display-mode-group { display:flex; flex-wrap:wrap; gap:16px 24px; }
  .display-mode-option { display:inline-flex; align-items:center; gap:12px; }
  .display-mode-custom-input { width: 260px; }
  .inline-clear-btn { padding:0; height:auto; color:#9b6a44; }
  .textarea-wrap { position: relative; }
  .textarea-counter {
    position: absolute; right: 14px; bottom: 12px; color: #8f7f6f; font-size: 13px; line-height: 1;
    pointer-events: none; background: rgba(255,250,244,.92); padding-left: 8px;
  }
  .textarea-wrap :deep(textarea.ant-input) { padding-bottom: 34px; }
  .team-card { margin-top:16px; padding:18px 20px; background:#fffaf4; border:1px dashed #e5d4bf; border-radius:18px; }
  .team-card-header { margin-bottom:14px; display:flex; align-items:center; justify-content:space-between; color:#46372a; font-weight:600; }
  .footer-actions { display:flex; justify-content:flex-end; gap:12px; margin-top:8px; }
  :deep(.ant-input),:deep(.ant-input-number),:deep(.ant-select-selection),:deep(.ant-input-affix-wrapper) { border-radius:12px; border-color:#e7d8c5; }
  :deep(.ant-radio-wrapper) { margin-right:18px; margin-bottom:6px; }
  :deep(.ant-btn-primary) { border-radius:999px; background:linear-gradient(90deg,#f3a15d 0%,#ef8f42 100%); border:none; box-shadow:0 12px 22px rgba(239,143,66,.24); }
  :deep(.ant-btn) { border-radius:999px; }
  @media (max-width: 900px) {
    width: calc(100vw - 20px);
    margin: 14px auto 28px;

    .page-top,
    .form-card,
    .result-card,
    .state-card {
      border-radius: 16px;
    }

    .page-top {
      margin-bottom: 14px;
      padding: 18px 14px;
    }

    .page-top-banner {
      padding: 0;
    }

    .hero-banner-image {
      border-radius: 16px;
    }

    .hero-title {
      font-size: 24px;
      line-height: 1.35;
    }

    .hero-meta,
    .result-actions,
    .inline-controls,
    .with-action,
    .label-row {
      flex-direction: column;
      align-items: flex-start;
    }

    .hero-meta {
      gap: 8px;
      font-size: 13px;
    }

    .result-card {
      padding: 34px 18px;
    }

    .result-title {
      font-size: 24px;
    }

    .result-actions {
      align-items: stretch;
    }

    .result-actions :deep(.ant-btn) {
      width: 100%;
    }

    .form-card {
      padding: 14px;
    }

    .step-bar {
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 6px;
      margin-bottom: 16px;
      padding: 10px 8px;
      border-radius: 14px;
    }

    .step-item {
      flex-direction: column;
      gap: 5px;
      text-align: center;
      font-size: 12px;
      line-height: 1.25;
    }

    .step-index {
      width: 22px;
      height: 22px;
      line-height: 22px;
    }

    .form-section {
      margin-bottom: 16px;
      padding: 16px 14px;
      border-radius: 16px;
    }

    .section-title {
      margin-bottom: 14px;
      font-size: 18px;
      line-height: 1.35;
    }

    .section-title::before {
      top: 3px;
      height: 22px;
    }

    .title-inline-group {
      align-items: flex-start;
      gap: 6px;
    }

    .section-tip,
    .upload-tip {
      font-size: 12px;
      line-height: 1.5;
    }

    .option-row {
      display: grid;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 10px;
    }

    .option-pill {
      min-width: 0;
      padding: 11px 10px;
      font-size: 13px;
      white-space: normal;
    }

    .graduation-category-row {
      display: flex;
      flex-wrap: nowrap;
      overflow-x: auto;
      padding-bottom: 6px;
      -webkit-overflow-scrolling: touch;
    }

    .graduation-category-row .option-pill {
      flex: 0 0 118px;
      white-space: nowrap;
    }

    .identity-card-row,
    .grid.two-col,
    .grid.three-col,
    .grid.four-col {
      grid-template-columns: 1fr;
    }

    .grid {
      gap: 14px;
    }

    .identity-card {
      padding: 15px 16px;
      border-radius: 16px;
    }

    .identity-card-title {
      font-size: 16px;
    }

    .inline-block {
      margin-top: 14px;
    }

    .power-supply-group,
    .display-mode-group {
      gap: 10px 12px;
    }

    .display-mode-option {
      width: 100%;
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }

    .display-mode-custom-input {
      width: 100%;
    }

    .team-card {
      margin-top: 12px;
      padding: 14px;
      border-radius: 16px;
    }

    .team-card-header {
      align-items: flex-start;
      gap: 8px;
    }

    .template-download-link {
      padding: 0;
      white-space: normal;
      text-align: left;
    }

    :deep(.ant-input),
    :deep(.ant-input-number),
    :deep(.ant-select-selection),
    :deep(.ant-input-affix-wrapper) {
      min-height: 40px;
    }

    :deep(.ant-radio-wrapper) {
      margin-right: 12px;
      white-space: normal;
      line-height: 1.8;
    }

    :deep(.image-list) {
      gap: 8px;
    }

    :deep(.image-item) {
      max-width: 100%;
    }

    :deep(.word-upload) {
      align-items: flex-start;
      flex-direction: column;
      gap: 8px;
    }

    .footer-actions {
      position: sticky;
      bottom: 10px;
      z-index: 4;
      align-items: stretch;
      margin: 4px -4px 0;
      padding: 10px;
      border: 1px solid rgba(239, 143, 66, 0.12);
      border-radius: 16px;
      background: rgba(255, 250, 244, 0.96);
      box-shadow: 0 12px 30px rgba(99, 61, 33, 0.12);
      backdrop-filter: blur(8px);
    }

    .footer-actions :deep(.ant-btn) {
      width: 100%;
      min-height: 40px;
    }
  }
}
</style>
